import React, { useEffect, useRef, useState } from 'react';
import DataGrid from 'devextreme-react/data-grid';
import FilterRow from 'components/common/DataGridDevEx/FilterRow';
import filterRow from './RecallCreditNoteDialogContent.filterRow';
import styles from './RecallCreditNoteDialogContent.styles';
import { withStyles } from '@material-ui/core';
import { IFilters, IRecallCreditNoteDialogContentHandle, IRecallCreditNoteDialogContentProps } from './RecallCreditNoteDialog.properties';
import { isNull } from 'utils/utils';
import { ENTER_KEY, ESCAPE, TAB } from 'utils/constants';
import { useGridKeyboardNavigation } from 'components/common/DataGridDevEx/DataGrid.hooks';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { useMemoizedKeyHandlers } from 'utils/hooks';
import { HotKeys } from 'react-hotkeys';
import { DATAGRIDCONSTANTS } from './RecallCreditNoteDialog.constants';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';
import { columns } from './constants';
import { useCNDispatch } from '../CreditNotes.context';
import { CreditNotesActionTypes } from '../CreditNotes.actions';
import { useRetrieveParkedCreditNotes } from 'api/creditNotes/creditNotes';

const RecallCreditNoteDialogContent = (props: IRecallCreditNoteDialogContentProps, ref: React.Ref<IRecallCreditNoteDialogContentHandle>) => {
    const { classes, recallButtonRef, selectedRow, setSelectedRow, setGridLength, onCancel, onRecall, onGridTabOut, onFilterButtonShiftTab, Username, Entity } = props;
    const dataGridRef = useRef<DataGrid>();

    const [loading, setLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState<IFilters>();
    const [filtersValuesSchema, setFiltersValuesSchema] = React.useState<any>({});
    const { isLoading: parkedCreditNotesDataLoading, data: parkedCreditNotesData, mutateAsync: parkedReceiptsMutation } = useRetrieveParkedCreditNotes();
    const filterButtonRef = React.useRef<HTMLButtonElement>();
    const { onKeyDown: gridOnKeyDown, onFocusedCellChanging } = useGridKeyboardNavigation();
    const [defaultFirstRowSelection, setDefaultFirstRowSelection] = React.useState<boolean>(false);
    const contextDispatch = useCNDispatch();

    React.useEffect(
        () => {
            setGridLength(dataGridRef.current?.instance?.getDataSource()?.items().length);
        },
        [dataGridRef.current?.instance?.getDataSource()?.items().length]);

    const recallParkedCreditNotes = React.useCallback(
        () => {
            setLoading(true);
            if (!isNull(selectedRow)) {
                contextDispatch({
                    [CreditNotesActionTypes.setCreditNote]: selectedRow?.WorkCreditNoteId
                });
                showSnackBar({ variant: 'success', message: 'Parked credit note recalled successfully.' });
                if (onRecall) {
                    onRecall();
                }
            } else {
                showSnackBar({ variant: 'error', message: 'Failed to recall parked credit note.' });
            }
            setLoading(false);
        },
        [selectedRow]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            focusFilterButton,
            focusGrid(): void {
                focusGrid();
            },
            recall: recallParkedCreditNotes,
        }),
        [selectedRow, recallParkedCreditNotes]
    );

    React.useEffect(
        () => {
            setDefaultFirstRowSelection(true);
        },
        []
    );

    useEffect(
        () => {
            setLoading(true);
            const _filters = {
                Entity,
                Username
            };

            setFilters(_filters);
            setFiltersValuesSchema(_filters);
            filterOnApply({ filters: _filters });
            setLoading(false);
        },
        []
    );

    useEffect(
        () => {
            if (loading || parkedCreditNotesDataLoading) {
                if (dataGridRef.current) {
                    dataGridRef.current.instance.beginCustomLoading('');
                }
            } else {
                dataGridRef.current.instance.endCustomLoading();
            }
        },
        [loading, parkedCreditNotesDataLoading]
    );

    const onEscape = React.useCallback(
        () => {
            if (onCancel) {
                onCancel();
            }
        },
        [onCancel]
    );

    const onEnter = React.useCallback(
        () => {
            if (recallButtonRef?.current &&
                dataGridRef.current.instance?.$element()?.[0]?.contains(document.activeElement)) {
                recallParkedCreditNotes();
            }
        },
        [dataGridRef?.current, recallParkedCreditNotes]
    );

    const handlers = useMemoizedKeyHandlers({
        Escape: onEscape,
        Enter: onEnter
    });

    const focusFilterButton = React.useCallback(
        () => {
            if (filterButtonRef.current) {
                filterButtonRef.current.focus();
            }
        },
        [filterButtonRef.current]
    );

    const handleKeyDown = React.useCallback(
        (e) => {

            const keyboardEvent = e.event as KeyboardEvent;

            if (keyboardEvent.keyCode === TAB && !keyboardEvent.shiftKey) {
                setTimeout(
                    () => {
                        onGridTabOut();
                    },
                    0
                );

                return;
            }

            if (keyboardEvent.keyCode === TAB && keyboardEvent.shiftKey) {
                setTimeout(
                    () => {
                        focusFilterButton();
                    },
                    0
                );

                return;
            }

            if (keyboardEvent.keyCode === ESCAPE) {
                onEscape();

                return;
            }

            if (keyboardEvent.keyCode === ENTER_KEY) {
                onEnter();

                return;
            }

            gridOnKeyDown(e);
        },
        [gridOnKeyDown, onEscape, onEnter, focusFilterButton]
    );

    const focusAndSelectFirstRowGrid = React.useCallback(
        () => {
            if (!dataGridRef.current) {
                return;
            }

            dataGridRef.current?.instance?.getScrollable()?.scrollTo(0);
            focusGrid();

            if (dataGridRef.current?.instance?.getDataSource()?.items()?.length > 0) {
                dataGridRef.current?.instance?.selectRowsByIndexes([0]);
            }
            setDefaultFirstRowSelection(false);
        },
        [dataGridRef.current, setDefaultFirstRowSelection]
    );

    const focusGrid = () => {
        setTimeout(
            () => {
                dataGridRef.current?.instance.focus();
            },
            0);
    };

    const onContentReady = React.useCallback(
        () => {
            if (defaultFirstRowSelection) {
                focusAndSelectFirstRowGrid();
            }
        },
        [defaultFirstRowSelection, focusAndSelectFirstRowGrid]
    );

    const filterOnApply = async ({ filters: appliedFilters }: { formName?: string; filters: IFilters }) => {
        const _appliedFilters = { ...appliedFilters, Entity: appliedFilters.Entity };
        setFilters(appliedFilters);
        if (appliedFilters) {
            await parkedReceiptsMutation({ Entity: _appliedFilters?.Entity, Username: _appliedFilters?.Username && _appliedFilters?.Username !== '' ? _appliedFilters?.Username.toString() : undefined, CustomerId: _appliedFilters?.CustomerId && _appliedFilters?.CustomerId.toString() !== '' ? _appliedFilters?.CustomerId.toString() : undefined });
            setDefaultFirstRowSelection(true);
        }
    };

    const handleOnSelectionChanged = React.useCallback(
        (e) => {
            const selectedRowData = e.selectedRowsData?.[0] ?? {};
            setSelectedRow(selectedRowData);
        },
        [setSelectedRow]
    );

    return (
        <HotKeys handlers={handlers}>
            <div className={classes.recallParkedSaleContainer}>
                <FilterRow
                    filterButtonRef={filterButtonRef}
                    disableInitialApplyCall={true}
                    parameters={filterRow.parameters}
                    formName={filterRow.formName}
                    onApplyFilters={filterOnApply}
                    initialValues={{ ...filters }}
                    valuesSchema={filtersValuesSchema}
                    validate={filterRow.validate}
                    rowModelType={filterRow.rowModelType}
                    onFilterButtonShiftTab={onFilterButtonShiftTab} />
                <DynamicDataGrid
                    dataGridClassName={classes.grid}
                    columns={columns}
                    allowHeaderFilting={false}
                    allowUpdating={true}
                    actionButtonsVisibility={false}
                    standardKeyboardNavigation={false}
                    gridProps={{
                        dataSource: parkedCreditNotesData?.ParkedCreditNotes ?? [],
                        dataGridRef: dataGridRef,
                        sorting: { mode: 'none' },
                        selection: { mode: 'single' },
                        disableDrillDown: true,
                        disableEditing: true,
                        disableDelete: true,
                        height: DATAGRIDCONSTANTS.GRID_HEIGHT,
                        keyExpr: 'WorkCreditNoteId',
                        onKeyDown: handleKeyDown,
                        onFocusedCellChanging: onFocusedCellChanging,
                        onContentReady: onContentReady,
                        onSelectionChanged: handleOnSelectionChanged
                    }}
                />
            </div>
        </HotKeys >
    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(RecallCreditNoteDialogContent)));
